import type { LookupRequest } from '@unifyapps/network/generated/models/lookupRequest';
import type { SchemaAndLayout } from '@unifyapps/network/generated/models/schemaAndLayout';
import type { BlockId, BlockType } from '../block';
import type { DataSource } from '../dataSource';
import type { Event } from '../event';
import { type InterfaceFolder, InterfaceType, type ApplicationType } from '../interface';
import type { DeviceVariantType } from '../deviceVariant';
import type { JavaScriptFunction } from '../javascriptFunction';
import type { JavaScriptVariable } from '../javascriptVariable';
import type { DevicesOverrides } from './deviceOverrides';
import type { Entity } from './entity';
import type { FieldType } from './field';
import type { WalkthroughType } from './walkthrough';

// Make sure that this type is in sync with the type defined in the API backend https://qa.unifyapps.com/api/entity-type?entityType=e_component

export type PageEventType = {
  eventType: string;
  label: string;
  description?: string;
  payload?: object;
};

export interface DataSourceDependencyType {
  type: 'DATA_SOURCE';
  dataSourceId: string;
}

export interface LookupRequestDependencyType {
  type: 'LOOKUP_REQUEST';
  lookupRequest: LookupRequest;
}

export type InterfaceDependencyType = DataSourceDependencyType | LookupRequestDependencyType;

export enum InterfacePageEntityComponentType {
  PAGE = 'PAGE',
  MODULE = 'MODULE',
  TEMPLATE_COMPONENT = 'TEMPLATE_COMPONENT',
  WALKTHROUGH = 'WALKTHROUGH',
  FORM = 'FORM',
  FIELD = 'FIELD',
}

export const MODULE_AND_TEMPLATE_INTERFACE_TYPES = new Set<InterfaceType>([
  InterfaceType.Module,
  InterfaceType.TemplateComponent,
]);

export const MODULE_AND_TEMPLATE_INTERFACE_PAGE_COMPONENT_TYPES =
  new Set<InterfacePageEntityComponentType>([
    InterfacePageEntityComponentType.MODULE,
    InterfacePageEntityComponentType.TEMPLATE_COMPONENT,
  ]);

export const FIELD_AND_FORM_INTERFACE_TYPES = new Set<InterfacePageEntityComponentType>([
  InterfacePageEntityComponentType.FIELD,
  InterfacePageEntityComponentType.FORM,
]);

export type DependsOnType = {
  /**
   * The unique identifier of the entity that this entity depends on.
   */
  id: string;
  /**
   * A list of property path from other entities that this entity relies on.
   * For example, if this entity uses values from a data source or another widget's state, it should be listed here.
   *
   * Example:
   * ```TypeScript
   * p: [
   *  "appState.user.name",
   *  "settings.defaultCurrency"
   * ]
   * ```
   */
  p: string[];
};

export type PageFieldDependency = {
  dpOn?: DependsOnType[];
  cP?: ConditionalPath[];
  dP?: DynamicPath[];
};

export type DynamicPath = {
  p: string;
};

export type ConditionalPath = {
  p: string;
};

export enum PageInternalVersion {
  // V0 means that we have included visibility and additional in the block state and calcuated block's dynamicPaths and conditionalPaths based on them
  V0,
  // V1 means that we have removed 0° rotation values from all blocks (base and device-based)
  V1,
  // V2 (DEPRECATED/RESERVED): originally "all block displayNames unique + camelCase".
  // The displayName rewrite broke references in saved pages and was reverted to a
  // no-op bump (see createDeprecatedMigration). Keep this slot — never reuse the number.
  V2,
}

/**
 * Template authoring metadata, mirrored onto the component so every
 * template list/picker query can read kind/platform/baseDevice from
 * `e_component` alone without joining the host interface.
 * `componentType` + the host interface's `manifest.type` / `deviceDetails.base`
 * remain the source of truth; these are query mirrors.
 */
export type TemplateMetadata = {
  kind?: InterfacePageEntityComponentType;
  platform?: ApplicationType;
  // page & module only — mobile has a single native form factor
  baseDevice?: DeviceVariantType;
  // blob-storage URL, never an inline data URL (avoids entity/collab bloat)
  previewImage?: string;
  // set when the template was cloned from an existing page
  linkedPageId?: string;
};

export type InterfacePageMetadata = {
  // NOTE: this is used for maintaining count for each block type, which is used in generating block ids
  // e.g., container_1, button_1, button_2, keyValue_1
  _blockCounter?: Record<string, number>;
  _folderCounter?: number;
  //[blockId]: { [blockProperty]: count }
  //eg: [table_1]: { column: 1 }
  _blockPropertyCounters?: Record<string, Record<string, number>>;
  // NOTE: this is used for maintaining count for each page event, which is used in generating page event
  _pageEventCounter?: number;
  // NOTE: this is used for dependencies between data sources and lookup requests, more to be added
  dependencies?: Partial<Record<string, string>>;
  dependencyById?: Partial<Record<string, InterfaceDependencyType>>;
  _version?: PageInternalVersion;
  _walkthroughStepCounter?: number;
  template?: TemplateMetadata;
};

export type CustomBlockVersions = {
  entityId: string;
  version: number;
};

export interface InterfacePageType {
  /**
   * The type of the component, helps in identifying the use case of because we use same entity to store multiple tenant's sitemap
   */
  componentType?: InterfacePageEntityComponentType;

  /**
   * Marks this component as a reusable template. Independent of `componentType`
   * (a template can be a PAGE, MODULE, or WALKTHROUGH) and of platform.
   */
  isTemplate?: boolean;

  /**
   * The InterfaceEntity this page belongs to
   */
  interfaceId?: string;
  /**
   * The type of the interface
   */
  interfaceType?: InterfaceType;
  /**
   * The name of the page / walkthrough
   */
  name?: string;
  /**
   * statusBar config for page
   */
  statusBar?: {
    style?: 'light' | 'dark' | 'auto';
  };
  /**
   * navigationBar config for page
   */
  navigationBar?: {
    style?: 'light' | 'dark' | 'auto';
  };
  /**
   * The description of the page / walkthrough
   */
  description?: string;
  /**
   * The title of the web page
   */
  pageTitle?: string;
  documentTitle?: string;
  /**
   * Indicates if the page should be publicly visible or restricted to logged-in users
   */
  publicAccess?: boolean;
  /**
   * The layout of the page including header and body
   */
  layout?: PageBody;
  /**
   * Block structure of the page
   */
  blocks?: Record<string, BlockType>;
  /**
   * @deprecated - Data sources for the page, not used anymore, we have e_data_source entity
   */
  dataSources?: Record<string, DataSource>;
  /**
   * Variables for the page
   */
  pageVariables?: Record<string, JavaScriptVariable>;
  /**
   * Functions for the page
   */
  pageFunctions?: Record<string, JavaScriptFunction>;

  /**
   * Folder structure of the page
   */
  folders?: Record<string, InterfaceFolder>;

  /**
   * The query param schema of the page
   */
  inputSchema?: SchemaAndLayout;

  /**
   * The query param schema of the page
   */
  outputSchema?: SchemaAndLayout;

  /**
   * Block IDs that template component authors have designated as eligible for consumer overrides.
   * Only present on TemplateComponent interface pages.
   */
  eligibleOverrides?: string[];

  /**
   * The output value of page
   */
  output?: Record<string, unknown>;

  /**
   * The custom events that the page will emit, a parent page can do handling based on these events
   */
  events?: PageEventType[];
  /**
   * The custom events that the page can receive from parent page
   */
  inboundEvents?: PageEventType[];
  /**
   * The slug of the page
   */
  slug?: string;

  /**
   * The permissions needed to access the page
   */
  permissions?: Record<string, string[]>;

  /**
   * The export layout of the page
   * includes the header and footer to be shown on each page
   */
  exportLayout?: PageBody;

  /**
   * Root block id of the shimmer loader tree for template component pages.
   * When set, the template component will render this subtree in place of the
   * body when its consumer sets the `loading` appearance flag to true.
   */
  loader?: BlockId;

  /**
   * Walkthrough settings and steps
   */
  walkthrough?: WalkthroughType;

  /**
   * Field configuration when componentType is FIELD (reusable field).
   */
  field?: FieldType;

  flags?: {
    /**
     * we are moving to save deps of blocks, page variables, page function and data sources.
     * this boolean marks the shift that for this page all the deps have been calculated and saved
     * if this is true, we don't compute the deps of a block at runtime in matrix
     */
    shouldUseBuiltDependencies?: boolean;
    /**
     * @deprecated
     * this is the new way we have written our code to work better without fully subscribing to the store
     */
    enablePagePubSubReactivity?: boolean;
  };

  metadata?: InterfacePageMetadata;
  devices?: DevicesOverrides;
  customCode?: { header?: string; footer?: string };
  interactions?: {
    events?: Event[];
    keyboardShortcuts?: Event[];
    inboundEvents?: Event[];
  };
  customBlocksVersionsMap?: Record<string, CustomBlockVersions>;

  // Stores dpOn for page fields like documentTitle
  dependencies?: Record<string, PageFieldDependency | undefined>;

  /**
   * Flat, deduped list of entity references used by blocks on this page.
   * Each entry carries an `id` and an `entityType` discriminator.
   * Auto-populated by the scanner on block changes (see scanEmbeddedEntityReferences).
   * Dedup key is composite (`entityType` + `id`) so the same id under different
   * entity types is preserved as two distinct entries.
   *
   * v1: TemplateComponent contributes templateComponentId (entityType
   * `interfaceComponent`) and templateInterfaceRecordId (entityType `interface`).
   */
  embeddedEntityReferences?: { id: string; entityType: string }[];
}

export interface PageBody {
  header?: BlockId;
  body?: BlockId;
  footer?: BlockId;
}

export type { DevicesOverrides };

export type InterfacePageEntity = Entity<InterfacePageType>;
